// 11 §3.1 / §11.1: RouteStore is driven by server rows. The party's `adventure_instance`
// (state 1 Loaded / 2 InEncounter) opens the LocalMap; the party's `encounter_id` opens the
// encounter; `encounter.state` 3 (Ended) routes back to the LocalMap while the instance is still
// under way, to the Aftermath once it has settled (state 3/4) or for a lobby quick fight; the
// user's "Back" clears the ids. The encounter subscription group is entered one route early.
// docs/21: a begun World whose story is still being written shows the writing screen (at least
// WRITING_MIN_MS before the intro, even for a story written ahead); a told story plays its intro once per browser while the opening waits (or for a late joiner); the opening's
// encounter ends on the epilogue for the players who fought it.
// docs/25 D9: every encounter that ends while a player is in it shows its Spoils screen first
// (outcome, XP, the division of the haul); Continue hands on to the destination above.
import { useEffect, useState } from 'react';
import { offTheRoute, useRoute } from '../core/stores/route';
import { useSession } from '../core/stores/session';
import { useBookChapters, useEncounterRow, useMyHeroes, useRewardDivision, useParties, usePartyInstances, useWorld, useWorldOpening, useWorldPartyMembers, useWorldSettings, useWorldStory } from '../core/subscriptions/hooks';
import { OPENING, STORY, useStoryMarks } from '../core/story/genesis';
import { enterEncounter, leaveEncounter, useEncounter } from '../core/encounter/session';
import { identityHex } from '../core/net/connection';
import { INST } from '../core/adventure/graph';
import { useOverlay } from './overlay';
import { useCinematic } from '../core/stores/cinematic';
import { useSpoils } from '../core/stores/spoils';
import { useWriting, WRITING_MIN_MS } from '../core/stores/writing';
import { deviceNow } from '../core/time';
import { pathHolds, PATH_HOLD_MAX_MS, usePathCurtain } from '../core/stores/pathCurtain';
import { OUTCOMES } from './screens/levelupData';
import type { StampTone } from './components';

const ENC_ENDED = 3;
/** The longest the route waits on the named-foe card (its own beats run ~15 s; a reader may linger). */
const ESCAPE_HOLD_MS = 60_000;
/** The longest an ended encounter waits for its division row before routing on without the Spoils screen. */
const DIVISION_WAIT_MS = 5_000;
/** docs/56 EP3: the longest the fight's end waits for its last pulse to play out (should playback stall). */
const PLAYBACK_HOLD_MS = 60_000;
/** A fight seen ending waits this long for its last resolution to be queued before trusting `busy`. */
const END_SETTLE_MS = 300;
/** Curtain tone per encounter outcome (OUTCOMES: Victory, Wipe, Fled, Surrendered, Stalemate, Abandoned). */
const OUTCOME_TONE: StampTone[] = ['victory', 'defeat', 'neutral', 'victory', 'neutral', 'neutral'];
/** e2e only (e2e/helpers.ts): specs that are not about the opening skip the writing screen and the intro. */
const SKIP_OPENING = (() => { try { return localStorage.getItem('fk.e2e.skipOpening') === '1'; } catch { return false; } })();

export function RouteDriver() {
  const worldId = useSession((s) => s.worldId);
  const me = useSession((s) => s.identity ?? '');
  const screen = useRoute((s) => s.screen);
  const partyId = useRoute((s) => s.partyId);
  const encounterId = useRoute((s) => s.encounterId);
  const instanceId = useRoute((s) => s.instanceId);
  const go = useRoute((s) => s.go);
  const parties = useParties(worldId);
  const members = useWorldPartyMembers(worldId);
  const party = parties.find((p) => p.id === partyId);
  const enc = useEncounterRow(encounterId ?? party?.encounterId);
  const instances = usePartyInstances(partyId);
  const active = instances.find((i) => i.state === INST.LOADED || i.state === INST.IN_ENCOUNTER);
  const tracked = instanceId !== undefined ? instances.find((i) => i.id === instanceId) : undefined;
  const trackedState = tracked?.state;
  // The title screen, the Play online hub and the collection log are outside the row-driven
  // routing: nothing pulls the player off them.
  const atTitle = offTheRoute(screen);
  const settings = useWorldSettings(worldId);
  const gathering = settings?.phase === 0;

  // docs/17 §3: a World still gathering lives in its lobby; once begun, the lobby hands over to the World.
  useEffect(() => {
    if (atTitle || !worldId || !settings) return;
    if (gathering && screen !== 'gathering') go('gathering');
    else if (!gathering && screen === 'gathering') go('world');
  }, [atTitle, worldId, settings, gathering, screen, go]);

  // docs/21: the story screens. Only the World screen and the story screens themselves are
  // re-routed; an encounter, a map or an aftermath is never interrupted by the tale.
  const story = useWorldStory(worldId);
  const opening = useWorldOpening(worldId);
  const world = useWorld(worldId);
  const replay = useRoute((s) => s.replay);
  const marks = useStoryMarks();
  const myHeroIds = useMyHeroes().filter((h) => h.worldId === worldId).map((h) => h.id);
  const fought = !!opening && opening.heroes.some((h) => myHeroIds.includes(h));
  const chapters = useBookChapters(worldId);
  const latestChapter = chapters[chapters.length - 1];
  const unseenChapter = world && latestChapter && latestChapter.scene.length > 0 && !marks.chapterSeen(world, latestChapter.number) ? latestChapter.number : undefined;
  const storyScreen = (() => {
    if (!worldId || !settings || gathering) return undefined;
    if (!story || story.state !== STORY.TOLD) return SKIP_OPENING ? undefined : 'writing';
    if (!opening || !world) return undefined;
    if (opening.phase === OPENING.DONE && fought && !marks.epilogueSeen(world)) return 'epilogue';
    // docs/155 RF1: a player the battle started without still finishes the tale, and may join it from there
    if (!SKIP_OPENING && !marks.introSeen(world) && (opening.phase === OPENING.WAITING || (opening.phase === OPENING.FIGHTING && !fought) || (opening.phase === OPENING.DONE && !fought))) return 'intro';
    // docs/86: a new storybook chapter's scene, once (the rules above keep the opening first)
    if (unseenChapter !== undefined) return 'chapter';
    return undefined;
  })();
  // the writing screen's floor: the hold starts the first time this World's opening comes up here
  const holdSince = useWriting((s) => (worldId !== undefined ? s.since[worldId.toString()] : undefined));
  const opens = storyScreen === 'writing' || storyScreen === 'intro';
  useEffect(() => { if (opens && worldId !== undefined) useWriting.getState().begin(worldId); }, [opens, worldId]);
  const [, setHoldTick] = useState(0);
  const holding = holdSince === undefined || deviceNow() < holdSince + WRITING_MIN_MS;
  useEffect(() => {
    if (!opens || holdSince === undefined || !holding) return;
    const t = setTimeout(() => setHoldTick((x) => x + 1), holdSince + WRITING_MIN_MS - deviceNow() + 20);
    return () => clearTimeout(t);
  }, [opens, holdSince, holding]);
  const storyWant = storyScreen === 'intro' && holding ? 'writing' : storyScreen;
  useEffect(() => {
    // the story marks need the World row: wait for it rather than open the World first
    if (atTitle || !worldId || !settings || gathering || (story?.state === STORY.TOLD && opening && !world)) return;
    if ((screen === 'intro' || screen === 'chapter') && replay) return;
    if (!['world', 'writing', 'intro', 'epilogue', 'chapter'].includes(screen)) return;
    const want = storyWant ?? 'world';
    if (want !== screen) go(want, { replay: undefined });
  }, [atTitle, worldId, settings, gathering, story?.state, opening, world, screen, replay, storyWant, go]);

  // No world → World screen.
  useEffect(() => { if (atTitle) return; if (!worldId && screen !== 'world') go('world', { partyId: undefined, encounterId: undefined, instanceId: undefined }); }, [atTitle, worldId, screen, go]);
  const parked = atTitle || gathering;

  // Auto-select the party one of my heroes is in (reload, second browser); the World and its
  // story screens keep their screen (the story effect above owns those). A selected party that has
  // disbanded (a band whose heroes went to the opening's fellowship) gives way to the one they are in.
  const disbanded = party?.state === 2;
  // docs/120 VM6: a band-mate who answered a call to come along on someone else's fresh band follows it
  const left = !!partyId && party?.state === 0 && !members.some((m) => m.partyId === partyId && identityHex(m.owner) === me) && members.some((m) => identityHex(m.owner) === me && m.partyId !== partyId && parties.some((p) => p.id === m.partyId && p.state !== 2));
  useEffect(() => {
    if (parked || !worldId || (partyId && !disbanded && !left)) return;
    const mine = members.find((m) => identityHex(m.owner) === me && parties.some((p) => p.id === m.partyId && p.state !== 2));
    if (mine) go(disbanded || left || ['world', 'village', 'writing', 'intro', 'epilogue', 'chapter'].includes(screen) ? screen : 'lobby', { partyId: mine.partyId });
  }, [parked, worldId, partyId, disbanded, left, members, parties, me, go, screen]);

  // An adventure is under way for my party: track it and open the LocalMap (unless a room is open).
  useEffect(() => {
    if (parked || !worldId || !active) return;
    if (instanceId !== active.id) go(active.encounterId ? screen : 'localmap', { instanceId: active.id });
  }, [parked, worldId, active, active?.id, active?.encounterId, instanceId, go, screen]);

  // docs/113 QP7: on a quest's path, the band walks to the stop and the map fades to black first
  // (the path screen lets go of the encounter, or the wait runs out)
  const pathHold = usePathCurtain((s) => screen === 'localmap' && !!party?.encounterId && pathHolds(s, instanceId, party.encounterId));
  const [pathWaited, setPathWaited] = useState<bigint>();
  useEffect(() => {
    if (!pathHold || !party?.encounterId) return;
    const id = party.encounterId;
    const t = setTimeout(() => setPathWaited(id), PATH_HOLD_MAX_MS);
    return () => clearTimeout(t);
  }, [pathHold, party?.encounterId]);
  const heldOnPath = pathHold && pathWaited !== party?.encounterId;

  // The party set out (quick fight or a site that opens a room): enter the encounter and route there.
  useEffect(() => {
    if (atTitle || !worldId || !party?.encounterId || heldOnPath) return;
    if (encounterId !== party.encounterId) go('encounter', { encounterId: party.encounterId });
  }, [atTitle, worldId, party?.encounterId, encounterId, go, heldOnPath]);

  useEffect(() => {
    if (!worldId || !encounterId || !partyId) return;
    enterEncounter(encounterId, partyId, worldId);
  }, [worldId, encounterId, partyId]);

  // docs/22 O4: an opening won on the board ends on the named-foe card (ui/encounter/Opening.tsx) before
  // the epilogue: hold the route while it plays (at most ESCAPE_HOLD_MS, should the card never show).
  const escapeSeen = useCinematic((s) => s.escapeSeen);
  const cineRev = useCinematic((s) => s.rev);
  const [holdExpired, setHoldExpired] = useState<bigint>();
  const holdForEscape = !!enc && !!opening && enc.id === opening.encounterId && enc.state === ENC_ENDED && screen === 'encounter'
    && (enc.outcome === 0 || enc.outcome === 3) && holdExpired !== enc.id && !escapeSeen(enc.id);
  useEffect(() => {
    if (!holdForEscape || !enc) return;
    const id = enc.id;
    const t = setTimeout(() => setHoldExpired(id), ESCAPE_HOLD_MS);
    return () => clearTimeout(t);
  }, [holdForEscape, enc]);

  // docs/25 D9: the encounter's division is written with Ended; its Spoils screen comes first. The
  // division is subscribed for the whole fight so it lands with Ended; should it lag, the route waits
  // (at most DIVISION_WAIT_MS) rather than skipping the screen.
  const division = useRewardDivision(enc?.id);
  const spoilsDone = useSpoils((s) => (encounterId !== undefined ? s.done.includes(encounterId.toString()) : false));
  const [divisionWaited, setDivisionWaited] = useState<bigint>();
  const awaitingDivision = !!enc && enc.state === ENC_ENDED && !division && screen === 'encounter' && divisionWaited !== enc.id;
  useEffect(() => {
    if (!awaitingDivision || !enc) return;
    const id = enc.id;
    const t = setTimeout(() => setDivisionWaited(id), DIVISION_WAIT_MS);
    return () => clearTimeout(t);
  }, [awaitingDivision, enc]);

  // docs/56 EP3/EP5: the fight's end waits for its last pulse to play out on the board (the board
  // shows how it ended), and a defeat waits on its card until the player continues
  const busy = useEncounter((s) => s.busy && s.encounterId === enc?.id);
  const defeat = useCinematic((s) => s.defeat);
  const defeatHeld = !!enc && defeat?.encounterId === enc.id && !defeat.continued && screen === 'encounter';
  const endId = enc && enc.state === ENC_ENDED && screen === 'encounter' ? enc.id : undefined;
  const [endQueued, setEndQueued] = useState<bigint>();
  const [playbackWaited, setPlaybackWaited] = useState<bigint>();
  useEffect(() => {
    if (endId === undefined) return;
    const t = setTimeout(() => setEndQueued(endId), END_SETTLE_MS);
    const cap = setTimeout(() => setPlaybackWaited(endId), PLAYBACK_HOLD_MS);
    return () => { clearTimeout(t); clearTimeout(cap); };
  }, [endId]);
  const holdForPlayback = endId !== undefined && playbackWaited !== endId && (endQueued !== endId || busy);

  // Ended → Spoils, then LocalMap (adventure still under way), the epilogue (the opening), the lobby
  // (a quick fight) or the Aftermath (a settled adventure); otherwise the board.
  useEffect(() => {
    if (atTitle || !encounterId || !enc) return;
    if (enc.state === ENC_ENDED) {
      if (holdForEscape || awaitingDivision || holdForPlayback || defeatHeld) return;
      if (division && !spoilsDone && (screen === 'encounter' || screen === 'spoils')) {
        if (screen !== 'spoils') go('spoils');
        return;
      }
      const inAdventure = enc.instanceId !== undefined && enc.instanceId !== null;
      if (inAdventure && tracked && trackedState !== undefined && trackedState <= INST.IN_ENCOUNTER) {
        if (screen !== 'localmap') {
          // The room ended while we watched it: stamp the result over the map as it comes back.
          // docs/141: the board stays up while the screen fades down; it is let go under the black.
          const stamp = screen === 'encounter' && enc.outcome !== undefined && enc.outcome !== null
            ? { key: encounterId.toString(), word: OUTCOMES[enc.outcome] ?? 'Ended', tone: OUTCOME_TONE[enc.outcome] ?? 'neutral', eyebrow: 'The room is done', sub: `${enc.name} · ${enc.pulseIndex} turn${enc.pulseIndex === 1 ? '' : 's'}` }
            : undefined;
          go('localmap', { encounterId: undefined }, { before: () => { if (stamp) useOverlay.getState().showOutcome(stamp); leaveEncounter(); } });
        }
      } else if (opening && enc.id === opening.encounterId) {
        // the first battle ends on its epilogue (the story, the rewards, the campaign's first quest)
        if (screen === 'encounter' || screen === 'spoils' || screen === 'aftermath') go(opening.phase === OPENING.DONE ? 'epilogue' : 'world', { encounterId: undefined, instanceId: undefined, partyId: undefined }, { before: leaveEncounter });
      } else if (screen === 'spoils' && !inAdventure) {
        // a quick fight: the Spoils screen was its aftermath
        go('lobby', { encounterId: undefined }, { before: leaveEncounter });
      } else if (screen !== 'aftermath') go('aftermath');
    } else if (screen !== 'encounter' && screen !== 'lobby' && screen !== 'world' && screen !== 'localmap') go('encounter');
    else if (screen !== 'encounter' && party?.encounterId === encounterId) go('encounter');
  }, [atTitle, encounterId, enc, enc?.state, enc?.instanceId, screen, go, party?.encounterId, tracked, trackedState, opening, holdForEscape, cineRev, division, spoilsDone, awaitingDivision, holdForPlayback, defeatHeld]);

  // The adventure settled while we were on its map (abandon, sanctum, second wipe): Aftermath.
  useEffect(() => {
    if (atTitle || !tracked || trackedState === undefined || trackedState < INST.COMPLETED) return;
    if (screen === 'localmap') go('aftermath');
  }, [atTitle, tracked, trackedState, screen, go]);

  return null;
}
